// sourceSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchPDF, SOURCE_FETCH_TIMEOUT_MS, SOURCE_DOWNLOAD_TIMEOUT_MS } from './pdfViewslice';
import { BASE_WORDURL } from '../components/constants';
import { authHeader } from '../utils/authHeaders';

// Loads a generated Word document's PDF into the same sidebar viewer used
// for regular document citations (Sidebar.jsx / PDFViwer.jsx just render
// whatever blob is in state.source.currentPdf — no indexed-document DB
// record required, see /word/download/{document_id}?format=pdf).
export const fetchGeneratedPdf = createAsyncThunk(
  'source/fetchGeneratedPdf',
  async ({ documentId, userId, filename }, { rejectWithValue }) => {
    const controller = new AbortController();
    let timer = setTimeout(() => controller.abort(), SOURCE_FETCH_TIMEOUT_MS);
    try {
      const response = await fetch(
        `${BASE_WORDURL}/download/${documentId}?format=pdf`,
        { headers: { ...authHeader() }, signal: controller.signal },
      );
      clearTimeout(timer);
      timer = setTimeout(() => controller.abort(), SOURCE_DOWNLOAD_TIMEOUT_MS);
      if (!response.ok) {
        return rejectWithValue({ message: 'Failed to load preview', code: response.status === 404 ? 'not_found' : 'load_failed' });
      }
      const blob = await response.blob();
      return { blob, filename };
    } catch (err) {
      const code = err?.name === 'AbortError' ? 'timeout' : 'load_failed';
      return rejectWithValue({ message: err.message || 'Failed to load preview', code });
    } finally {
      clearTimeout(timer);
    }
  }
);

const initialState = {
  selectedSources: [],
  currentPdf: {
    filename: null,
    page: null,
    itemId: null,
    blob: null,
  },
  loading: false,
  error: null,
  errorCode: null
};

const sourceSlice = createSlice({
  name: 'source',
  initialState,
  reducers: {
    addSource: (state, action) => {
      const { filename, page, itemId } = action.payload;
      state.loading = true;
      state.currentPdf.filename = filename;
      state.currentPdf.page = page;
      // SharedView.jsx reads this back to fetch the PDF with the owner's
      // credentials (ChatBlob's own direct fetchPDF call doesn't fire there
      // — no userId/taskId for an anonymous share viewer) — without it here,
      // that fetch can't disambiguate two same-named files either.
      state.currentPdf.itemId = itemId || null;
      state.currentPdf.blob = null;
      state.errorCode = null;

      const sourceExists = state.selectedSources.some(
        source => source.filename === filename && source.page === page
      );

      if (!sourceExists) {
        state.selectedSources.push({ filename, page, itemId });
      }
    },
    removeSource: (state, action) => {
      const { filename, page } = action.payload;
      state.selectedSources = state.selectedSources.filter(
        source => !(source.filename === filename && source.page === page)
      );
    },
    clearSources: (state) => {
      state.selectedSources = [];
      state.currentPdf = {
        filename: null,
        page: null,
        itemId: null,
        blob: null,
      };
      state.loading = false;
      state.error = null;
      state.errorCode = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPDF.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(fetchPDF.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPdf.blob = action.payload;
      })
      .addCase(fetchPDF.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
        state.errorCode = action.payload?.code || null;
      })
      .addCase(fetchGeneratedPdf.pending, (state) => {
        state.loading = true;
        state.currentPdf = { filename: null, page: null, itemId: null, blob: null };
        state.error = null;
        state.errorCode = null;
      })
      .addCase(fetchGeneratedPdf.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPdf = {
          filename: action.payload.filename,
          page: 1,
          itemId: null,
          blob: action.payload.blob,
        };
      })
      .addCase(fetchGeneratedPdf.rejected, (state, action) => {
        state.loading = false;
        state.currentPdf = { filename: action.meta.arg.filename, page: 1, itemId: null, blob: null };
        state.error = action.payload?.message || action.error.message;
        state.errorCode = action.payload?.code || null;
      });
  },
});

export const { addSource, removeSource, clearSources } = sourceSlice.actions;
export default sourceSlice.reducer;